import React, { useCallback, useEffect, useState, useRef, useId, Component } from 'react';
import { cn } from '@/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, CheckCircle2, Clock } from 'lucide-react';
import { getRegionMeta } from '../utils/region';
// Reusable address autocomplete input.
// - Queries Photon (https://photon.komoot.io/) — free, no API key, fast,
//   autocomplete-optimized OSM search. Biased to the South Sound delivery area.
// - Renders a styled suggestion dropdown with keyboard navigation.
// - When the user picks a suggestion, fires `onSelect(address, lat, lon)`
//   so the parent can skip re-geocoding and act on the coordinates directly.
// - Falls back to the parent's existing debounced geocode if the user types
//   without picking a suggestion.
const PHOTON_URL = 'https://photon.komoot.io/api/';
export type AddressSuggestion = {
  label: string;
  fullAddress: string;
  lat: number;
  lon: number;
};
type Props = {
  value: string;
  onChange: (val: string) => void;
  onSelect?: (suggestion: AddressSuggestion) => void;
  placeholder?: string;
  inputClassName?: string;
  containerClassName?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isLoading?: boolean;
  required?: boolean;
  autoComplete?: string;
  inputId?: string;
  inputAriaLabel?: string;
  /** Style for the suggestion dropdown ("dark" matches the Hero glass card,
   * "light" matches light surfaces). */
  variant?: 'dark' | 'light';
  /** Open the dropdown above the input instead of below. Useful for
   * inputs anchored to the bottom of the viewport. */
  dropDirection?: 'down' | 'up';
  /** Previously-used addresses to surface as "recent" suggestions when the
   * field is focused but still empty (before the user types). Picking one
   * fills the input and lets the parent re-geocode it. */
  recentAddresses?: string[];
  /** Fired when the user explicitly picks one of the `recentAddresses`.
   * Unlike a live suggestion (which carries coordinates via `onSelect`), a
   * recent address has no stored lat/lon, so the parent must re-geocode it.
   * This callback signals that the fill was an EXPLICIT confirmation (not
   * mid-typing), so the parent can open downstream steps once the geocode
   * resolves. */
  onSelectRecent?: (addr: string) => void;
};
type PhotonFeature = {
  geometry: {
    coordinates: [number, number];
  };
  properties: {
    osm_id?: number;
    name?: string;
    housenumber?: string;
    street?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
    countrycode?: string;
    type?: string;
    osm_value?: string;
  };
};
// OSM internal identifiers leak through Photon when the underlying way
// or node has no real name. They look like "W-12345 Road", "N-678",
// "R-90" — never a real-world address.
const OSM_ID_NAME = /^[WNR]-\d+/i;
function formatSuggestion(f: PhotonFeature): AddressSuggestion | null {
  const p = f.properties;
  if (p.countrycode && p.countrycode !== 'US') return null;
  if (p.state && p.state !== 'Washington') return null;
  // REQUIRE a real street. POIs, lakes, parks, and OSM-id-labeled ways
  // (e.g. "W-1280 Road") are not addresses and just confuse customers
  // typing into a delivery field. If a feature has no street, drop it.
  if (!p.street) return null;
  const street = [p.housenumber, p.street].filter(Boolean).join(' ').trim();
  if (!street) return null;
  // Defense in depth — reject any leftover OSM-id-shaped names that
  // somehow slipped into the street field.
  if (OSM_ID_NAME.test(street)) return null;
  const cityBit = [p.city, p.state ? 'WA' : undefined, p.postcode].filter(Boolean).join(', ');
  const full = [street, cityBit].filter(Boolean).join(', ');
  return {
    label: street,
    fullAddress: full,
    lat: f.geometry.coordinates[1],
    lon: f.geometry.coordinates[0]
  };
}
export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  inputClassName = '',
  containerClassName = '',
  leftIcon,
  rightIcon,
  isLoading = false,
  required,
  autoComplete = 'street-address',
  inputId,
  inputAriaLabel,
  variant = 'dark',
  dropDirection = 'down',
  recentAddresses = [],
  onSelectRecent
}: Props) {
  const regionCenter = getRegionMeta().center || [47.2073, -122.197];
  const BIAS_LAT = regionCenter[0];
  const BIAS_LON = regionCenter[1];
  const reactId = useId();
  const listboxId = `${inputId || reactId}-listbox`;
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number>(-1);
  const [isFetching, setIsFetching] = useState(false);
  // True while the input holds focus. Used to surface the "recent addresses"
  // list when the field is focused but still empty.
  const [focused, setFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // The address text input — held so we can blur() it (dismissing the mobile
  // keyboard) the moment the customer confirms an address, letting the page
  // advance cleanly to the next section instead of leaving the keyboard up.
  const inputRef = useRef<HTMLInputElement>(null);
  // Track the value the parent just received from a user pick — used to
  // suppress the next debounced fetch (which would just re-search for the
  // selected address).
  const justSelectedRef = useRef(false);
  // Track whether the current value came from the user actually typing in
  // this input (vs. the parent programmatically pre-filling it — e.g. when
  // the Delivery Zones map hydrates an address the customer already
  // confirmed in the estimator). We only fetch suggestions / open the
  // dropdown for user-typed input, so a pre-filled address never re-prompts
  // a "Tap to confirm" the customer has already done.
  const userTypedRef = useRef(false);
  // Close on click outside.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setActiveIdx(-1);
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  // Debounced Photon query as the user types.
  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    // Programmatic pre-fill (parent set the value, user hasn't typed here):
    // don't fetch or open the dropdown. The address is already confirmed.
    if (!userTypedRef.current) {
      return;
    }
    const q = value.trim();
    // Don't query until the customer has typed enough to form a real
    // street query. A bare house number ("1280") returns OSM noise —
    // require at least 3 characters AND at least one letter so we know
    // the user has started typing a street name.
    if (q.length < 3 || !/[a-zA-Z]/.test(q)) {
      setSuggestions([]);
      setOpen(false);
      setActiveIdx(-1);
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      setIsFetching(true);
      try {
        const url = `${PHOTON_URL}?q=${encodeURIComponent(q)}&lat=${BIAS_LAT}&lon=${BIAS_LON}&limit=6&lang=en`;
        const res = await fetch(url, {
          signal: ctrl.signal
        });
        const data = await res.json();
        const feats: PhotonFeature[] = data?.features || [];
        const mapped: AddressSuggestion[] = [];
        const seen = new Set<string>();
        for (const f of feats) {
          const s = formatSuggestion(f);
          if (!s) continue;
          if (seen.has(s.fullAddress)) continue;
          seen.add(s.fullAddress);
          mapped.push(s);
          if (mapped.length >= 5) break;
        }
        setSuggestions(mapped);
        setOpen(mapped.length > 0);
        setActiveIdx(-1);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setSuggestions([]);
          setOpen(false);
        }
      } finally {
        setIsFetching(false);
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [value]);
  const handleSelect = useCallback((s: AddressSuggestion) => {
    justSelectedRef.current = true;
    onChange(s.fullAddress);
    setSuggestions([]);
    setOpen(false);
    setActiveIdx(-1);
    setFocused(false);
    // Dismiss the mobile keyboard so the confirmed address advances the
    // flow to the next section instead of staying behind the keyboard.
    inputRef.current?.blur();
    onSelect?.(s);
  }, [onChange, onSelect]);
  // Pick a recent address. We have no stored coordinates for these, so we
  // just fill the value and let the parent's debounced geocode resolve the
  // zone/fee — same path as if the user had typed it and stopped.
  const handleSelectRecent = useCallback((addr: string) => {
    justSelectedRef.current = true;
    // A recent-address pick has no stored coordinates, so the parent must
    // re-geocode it. Mark this as a USER-driven fill (not a programmatic
    // pre-fill) so the debounced geocode effect runs and resolves the zone.
    userTypedRef.current = true;
    onChange(addr);
    setOpen(false);
    setActiveIdx(-1);
    setFocused(false);
    // Dismiss the mobile keyboard on confirm so the flow advances to the
    // next section rather than leaving the keyboard covering it.
    inputRef.current?.blur();
    // Signal the parent that this was an explicit confirmation so it can
    // open downstream steps once the geocode resolves.
    onSelectRecent?.(addr);
  }, [onChange, onSelectRecent]);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // ALWAYS swallow Enter inside the address field. This input lives inside
    // the estimator's <form>, so a bare Enter would natively submit the form
    // (or trigger whatever the browser treats as the implicit submit) the
    // moment the address box "fires" — which read to the user like an
    // unintended reset/Start-over. The estimator advances via its own
    // buttons, never via Enter on the address, so we prevent the default
    // submit here regardless of whether the suggestion dropdown is open.
    if (e.key === 'Enter') {
      e.preventDefault();
      if (open && activeIdx >= 0 && activeIdx < suggestions.length) {
        handleSelect(suggestions[activeIdx]);
      }
      return;
    }
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => i <= 0 ? suggestions.length - 1 : i - 1);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIdx(-1);
    }
  };
  // Dropdown treatment — sits below the input as its own clean
  // green-bordered pill, mirroring the input's rounded outline so the
  // two read as a matched pair. Green border + green option labels
  // make the list immediately legible as "tappable verified-area
  // addresses".
  // Dropdown surface deliberately MATCHES the input pill tone (white in
  // light, #1a1a1a in dark) so the suggestion list reads as a seamless
  // continuation of the field rather than a separate green/grey panel.
  const dropdownStyles = variant === 'light' ? 'bg-white border-white text-[#00704A] dark:bg-[#2A7850] dark:border-[#2A7850] dark:text-white shadow-xl' : 'bg-[#006239] backdrop-blur-md border-[#22c95c]/45 text-white shadow-xl';
  const itemHover = variant === 'light' ? 'hover:bg-[#00704A]/[0.06] dark:hover:bg-white/[0.10]' : 'hover:bg-white/[0.06]';
  const itemActive = variant === 'light' ? 'bg-[#00704A]/[0.06] dark:bg-white/[0.10]' : 'bg-white/[0.06]';
  const subText = variant === 'light' ? 'text-stone-500' : 'text-white/55';
  // Suggestion street label matches the typed address above (white in dark,
  // near-black in light) — the only green accent on the row is the
  // "Tap to confirm" affordance, so the row no longer looks like a green
  // duplicate of what was typed.
  const optionLabelColor = variant === 'light' ? 'text-stone-900' : 'text-white';
  // Show the recent-addresses list when the field is focused, still empty,
  // and we have history to offer (and the live typed-suggestion list isn't up).
  const showRecent = focused && value.trim() === '' && recentAddresses.length > 0 && suggestions.length === 0;
  const isMerged = open && suggestions.length > 0 || showRecent;
  return <div className={`relative ${containerClassName} ${isMerged ? 'address-autocomplete-merged' : ''}`} ref={containerRef} data-id="element-136" data-mp="34kp92jmntlhw">
      {/* Input-row wrapper — keeps the absolutely-positioned left and
                                                                                 right icons anchored to JUST the input's height. Without this
                                                                                 wrapper the icons span the full merged-pill height and end up
                                                                                 floating over the suggestion rows when the dropdown is open. */}
      <div className="relative" data-id="element-137" data-mp="p6dd0hvg9p82">
        {leftIcon && <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10" data-id="element-138" data-mp="p529mj5x2jhh">
            {leftIcon}
          </div>}
        <input ref={inputRef} id={inputId} type="text" value={value} onChange={e => {
        userTypedRef.current = true;
        onChange(e.target.value);
      }} onFocus={() => {
        setFocused(true);
        if (suggestions.length > 0) setOpen(true);
      }} onKeyDown={handleKeyDown} placeholder={placeholder} autoComplete={autoComplete}
      // Suppress browser / password-manager autofill popups so they
      // can't overlay our own recent-addresses + suggestion list. The
      // non-address `name` (combined with a non-standard autoComplete
      // token from the parent) keeps Chrome from recognizing this as a
      // saved-address field, and the data-* attributes opt out of
      // 1Password / LastPass overlays.
      name={inputId ? `${inputId}-q` : 'mp-address-q'} data-1p-ignore="true" data-lpignore="true" data-form-type="other" required={required} aria-label={inputAriaLabel} aria-autocomplete="list" aria-expanded={open || showRecent} aria-controls={listboxId} aria-activedescendant={activeIdx >= 0 ? `${listboxId}-opt-${activeIdx}` : undefined} role="combobox" className={cn(inputClassName)} data-id="element-139" data-mp="g709s6xx5wvs" />
        {(isLoading || isFetching) && <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none z-10" data-id="element-140" data-mp="1pq8h9b8taogn">
            <Loader2 className="w-4 h-4 text-[#00c06c] animate-spin" data-id="element-141" data-mp="1fylvikyul6zn" />
          </div>}
        {!isLoading && !isFetching && rightIcon && <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none z-10" data-id="element-142" data-mp="11yb6zol0g4hn">
            {rightIcon}
          </div>}
      </div>
      <AnimatePresence data-id="element-143" data-mp="1700foxkrg62k">
        {open && suggestions.length > 0 && <motion.ul id={listboxId} role="listbox" initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.15
      }} className="relative left-0 right-0 z-50 overflow-hidden" data-id="element-144" data-mp="nd6zb4louw5f">
            {suggestions.map((s, i) => {
          const active = i === activeIdx;
          return <li id={`${listboxId}-opt-${i}`} key={`${s.lat},${s.lon},${i}`} role="option" aria-selected={active} onMouseDown={e => {
            // mousedown (not click) so we beat the blur that would
            // otherwise close the dropdown before the click fires.
            e.preventDefault();
            handleSelect(s);
          }} onMouseEnter={() => setActiveIdx(i)} style={{
            WebkitTapHighlightColor: 'transparent',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
            // Dark-slate-green dropdown surface (matches the material
            // dropdown's #006239). Deliberately NOT the brand #006239
            // green — the "Shop by project" CTA below uses #006239, and
            // a matching green dropdown read as an indistinguishable
            // duplicate of that button. The active/highlighted row
            // lifts one subtle step brighter.
            backgroundColor: variant === 'light' ? 'transparent' : active ? '#3A423C' : '#2F3530'
          }} className={`select-none px-4 py-3 text-sm cursor-pointer transition-colors ${active ? itemActive : itemHover} ${variant === 'light' ? 'address-suggestion-row' : ''} ${variant === 'dark' ? 'border-t border-white/10 first-of-type:border-t-0' : 'border-t border-[#00704A]/10 dark:border-white/15 first-of-type:border-t-0'}`} data-id="element-145" data-mp="169yi7cxbj98y" data-mpi={i}>
                  <div className="flex items-start justify-between gap-3" data-id="element-146" data-mp="3c5smkzsccrmr">
                    <div className="min-w-0 flex-1" data-id="element-147" data-mp="3g6ckw6oni1ah">
                      <div className={`font-normal leading-tight text-sm ${optionLabelColor}`} data-id="element-148" data-mp="3g6w55u1rx1ro">
                        {s.label}
                      </div>
                      <div className={`text-sm mt-0.5 leading-tight font-normal ${subText}`} data-id="element-149" data-mp="33vv5zshxr1dj">
                        {s.fullAddress.replace(`${s.label}, `, '') || s.fullAddress}
                      </div>
                    </div>
                    {/* Confirm affordance — a bright-green check icon that
                     signals "tap to confirm this address". Always visible so
                     the row reads as a confirmable suggestion; brightens on
                     the active/highlighted row. */}
                    <CheckCircle2 aria-label="Confirm address" className={`shrink-0 mt-0.5 w-6 h-6 transition-opacity ${variant === 'dark' ? 'text-[#2EE56B]' : 'text-[#00704A]'} ${active ? 'opacity-100' : 'opacity-80'}`} data-id="element-150" data-mp="3ik83itwo8sa5" />
                  </div>
                </li>;
        })}
          </motion.ul>}
      </AnimatePresence>
      {/* Recent addresses — shown on focus when the field is empty. Sourced
                                   from the customer's prior orders, rendered in the same merged-pill
                                   style as the live suggestions. Picking one fills the field and lets
                                   the parent geocode it. */}
      <AnimatePresence data-id="element-151" data-mp="14s0dn0v21s0">
        {showRecent && <motion.ul role="listbox" aria-label="Recent addresses" initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.15
      }} className="relative left-0 right-0 z-50 overflow-hidden" data-id="element-152" data-mp="15l229kyq13a">
            {/* No "Recent" heading — the clock icon on each row already
                signals these are previously-used addresses. */}
            {recentAddresses.map((addr, i) => <li key={`recent-${i}`} role="option" aria-selected={false} onMouseDown={e => {
          // mousedown beats the input blur so the pick lands before
          // the dropdown closes.
          e.preventDefault();
          handleSelectRecent(addr);
        }} style={{
          WebkitTapHighlightColor: 'transparent',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          // Dark-slate-green surface (#006239) — same as the live
          // suggestion rows, distinct from the #006239 "Shop by
          // project" CTA below so the two don't read as identical.
          backgroundColor: variant === 'light' ? 'transparent' : '#2F3530'
        }} className={`select-none px-4 py-3 text-sm cursor-pointer transition-colors ${itemHover} ${variant === 'light' ? 'address-suggestion-row' : ''} ${variant === 'dark' ? 'border-t border-white/10' : 'border-t border-[#00704A]/10 dark:border-white/15'}`} data-id="element-153" data-mp="17sglggehhxtl" data-mpi={i}>
                <div className="flex items-center gap-3" data-id="element-154" data-mp="2lflic1fdw5x">
                  <Clock aria-hidden="true" className={`shrink-0 w-4 h-4 ${variant === 'dark' ? 'text-white/45' : 'text-[#00704A]/60'}`} data-id="element-155" data-mp="2o1sa9ghs7n3" />
                  <div className={`min-w-0 flex-1 font-normal leading-tight text-sm truncate ${optionLabelColor}`} data-id="element-156" data-mp="3jicqt7kf9qjl">
                    {addr}
                  </div>
                </div>
              </li>)}
          </motion.ul>}
      </AnimatePresence>
    </div>;
}